import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
  LayoutDashboard, Users, BookOpen, GraduationCap, Globe,
  Smartphone, Settings, LogOut, ChevronLeft, ChevronRight,
  Sparkles, Shield, Key, Compass, Lock, Eye, EyeOff, Video, Database, Image
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ADMIN_PATH = '/j93jdk-3-332d-43faa2-3';

const sidebarItems = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, path: ADMIN_PATH },
  { id: 'users', label: 'Users', icon: Users, path: `${ADMIN_PATH}/users` },
  { id: 'courses', label: 'Courses', icon: GraduationCap, path: `${ADMIN_PATH}/courses` },
  { id: 'books', label: 'Books', icon: BookOpen, path: `${ADMIN_PATH}/books` },
  { id: 'guides', label: 'Guides', icon: Compass, path: `${ADMIN_PATH}/guides` },
  { id: 'apps', label: 'Apps', icon: Smartphone, path: `${ADMIN_PATH}/apps` },
  { id: 'websites', label: 'Websites', icon: Globe, path: `${ADMIN_PATH}/websites` },
  { id: 'channels', label: 'Channels', icon: Video, path: `${ADMIN_PATH}/channels` },
  { id: 'gallery', label: 'Gallery', icon: Image, path: `${ADMIN_PATH}/gallery` },
  { id: 'data', label: 'Data I/O', icon: Database, path: `${ADMIN_PATH}/data` },
  { id: 'sessions', label: 'Sessions', icon: Key, path: `${ADMIN_PATH}/sessions` },
  { id: 'settings', label: 'Settings', icon: Settings, path: `${ADMIN_PATH}/settings` },
];

const AdminLayout = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [isAdmin, setIsAdmin] = useState(null); // null = loading, true/false = result
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const verifyAdmin = async () => {
      try {
        await axios.get(`${API}/admin/verify`, { withCredentials: true });
        setIsAdmin(true);
      } catch {
        setIsAdmin(false);
      }
    };
    verifyAdmin();
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoginLoading(true);
    try {
      await axios.post(`${API}/admin/login`, { password }, { withCredentials: true });
      setIsAdmin(true);
    } catch (err) {
      setLoginError(err.response?.data?.detail || 'Invalid password');
    }
    setLoginLoading(false);
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/admin/logout`, {}, { withCredentials: true });
    } catch {}
    setIsAdmin(false);
    setPassword('');
  };

  // Loading state
  if (isAdmin === null) {
    return (
      <div style={{ minHeight: '100vh', background: '#0a0716', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: 40, height: 40, border: '3px solid #7c3aed', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px' }} />
          <p style={{ color: '#8885a0', fontSize: 14 }}>Verifying admin access...</p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  // Password login screen
  if (!isAdmin) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#0a0716',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
      }}>
        <div style={{
          background: 'rgba(88,28,135,0.12)',
          border: '1px solid rgba(139,92,246,0.25)',
          borderRadius: 20,
          padding: '48px 40px',
          width: '100%',
          maxWidth: 400,
          textAlign: 'center',
          backdropFilter: 'blur(10px)',
        }}>
          <div style={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #7c3aed, #a855f7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px',
            boxShadow: '0 0 40px rgba(139,92,246,0.3)',
          }}>
            <Shield size={28} color="#ffd700" />
          </div>

          <h2 style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: 28,
            fontWeight: 700,
            marginBottom: 6,
            color: '#fff',
          }}>
            Admin Panel
          </h2>
          <p style={{ color: '#8885a0', marginBottom: 32, fontSize: 14 }}>
            Enter the admin password to continue
          </p>

          <form onSubmit={handleLogin}>
            <div style={{
              position: 'relative',
              marginBottom: 20,
            }}>
              <Lock size={18} color="#8885a0" style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)' }} />
              <input
                type={showPassword ? 'text' : 'password'}
                data-testid="admin-password-input"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setLoginError(''); }}
                placeholder="Enter admin password"
                autoFocus
                style={{
                  width: '100%',
                  padding: '14px 48px 14px 46px',
                  borderRadius: 12,
                  border: loginError ? '1px solid rgba(239,68,68,0.5)' : '1px solid rgba(139,92,246,0.3)',
                  background: 'rgba(88,28,135,0.15)',
                  color: '#fff',
                  fontSize: 15,
                  outline: 'none',
                  fontFamily: 'Inter, sans-serif',
                  boxSizing: 'border-box',
                }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: 14,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  color: '#8885a0',
                  cursor: 'pointer',
                  padding: 4,
                }}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>

            {loginError && (
              <p style={{ color: '#ef4444', fontSize: 13, marginBottom: 16 }}>{loginError}</p>
            )}

            <button
              type="submit"
              data-testid="admin-login-submit"
              disabled={loginLoading || !password}
              style={{
                width: '100%',
                padding: '14px',
                borderRadius: 12,
                border: 'none',
                background: 'linear-gradient(135deg, #7c3aed, #a855f7)',
                color: '#fff',
                fontSize: 15,
                fontWeight: 600,
                cursor: loginLoading || !password ? 'default' : 'pointer',
                opacity: loginLoading || !password ? 0.5 : 1,
                fontFamily: 'Inter, sans-serif',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
              }}
            >
              <Lock size={16} />
              {loginLoading ? 'Verifying...' : 'Access Admin Panel'}
            </button>
          </form>

          <button
            onClick={() => navigate('/')}
            style={{
              background: 'none',
              border: 'none',
              color: '#00d4ff',
              marginTop: 24,
              cursor: 'pointer',
              fontSize: 13,
              fontFamily: 'Inter, sans-serif',
            }}
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  // Admin authenticated — show panel
  const activeItem = sidebarItems.find(item => item.path === location.pathname) || sidebarItems[0];

  return (
    <div style={{ minHeight: '100vh', background: '#0a0716', display: 'flex' }}>
      {/* Sidebar */}
      <aside style={{
        width: collapsed ? 72 : 260,
        background: '#0f0a1e',
        borderRight: '1px solid rgba(139,92,246,0.15)',
        display: 'flex',
        flexDirection: 'column',
        transition: 'width 0.2s ease',
        flexShrink: 0,
        position: 'fixed',
        top: 0,
        left: 0,
        bottom: 0,
        zIndex: 50,
      }}>
        {/* Logo */}
        <div style={{ padding: collapsed ? '20px 12px' : '20px 24px', borderBottom: '1px solid rgba(139,92,246,0.1)', display: 'flex', alignItems: 'center', gap: 10, minHeight: 68 }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: 'linear-gradient(135deg,#7c3aed,#a855f7)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Sparkles size={18} color="#ffd700" />
          </div>
          {!collapsed && <span style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, fontWeight: 700 }}><span style={{ color: '#00d4ff' }}>Admin</span> <span style={{ color: '#ffd700' }}>Panel</span></span>}
        </div>

        {/* Nav */}
        <nav style={{ flex: 1, padding: '12px 8px', overflowY: 'auto' }}>
          {sidebarItems.map(item => {
            const Icon = item.icon;
            const active = location.pathname === item.path;
            return (
              <button key={item.id} onClick={() => navigate(item.path)} style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 12,
                padding: collapsed ? '12px' : '12px 16px',
                justifyContent: collapsed ? 'center' : 'flex-start',
                borderRadius: 10, border: 'none', marginBottom: 4, cursor: 'pointer',
                background: active ? 'rgba(139,92,246,0.2)' : 'transparent',
                color: active ? '#c4b5fd' : '#8885a0',
                fontSize: 14, fontWeight: active ? 600 : 400, fontFamily: 'Inter, sans-serif',
                transition: 'background 0.15s',
              }}
                onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'rgba(139,92,246,0.08)'; }}
                onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
              >
                <Icon size={20} />
                {!collapsed && item.label}
              </button>
            );
          })}
        </nav>

        {/* Collapse + Logout */}
        <div style={{ padding: '12px 8px', borderTop: '1px solid rgba(139,92,246,0.1)' }}>
          <button onClick={() => setCollapsed(!collapsed)} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'flex-start', gap: 12, padding: '10px 16px', borderRadius: 10, border: 'none', background: 'none', color: '#8885a0', cursor: 'pointer', fontSize: 13, fontFamily: 'Inter, sans-serif' }}>
            {collapsed ? <ChevronRight size={18} /> : <><ChevronLeft size={18} /> Collapse</>}
          </button>
          <button onClick={handleLogout} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'flex-start', gap: 12, padding: '10px 16px', borderRadius: 10, border: 'none', background: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 13, fontFamily: 'Inter, sans-serif', marginTop: 4 }}>
            <LogOut size={18} />
            {!collapsed && 'Logout'}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main style={{ flex: 1, marginLeft: collapsed ? 72 : 260, transition: 'margin-left 0.2s ease' }}>
        {/* Top Bar */}
        <header style={{ height: 68, background: '#0f0a1e', borderBottom: '1px solid rgba(139,92,246,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 32px', position: 'sticky', top: 0, zIndex: 40 }}>
          <h1 style={{ fontSize: 20, fontWeight: 700, fontFamily: "'Playfair Display', serif", color: '#fff' }}>{activeItem.label}</h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'linear-gradient(135deg,#7c3aed,#a855f7)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Shield size={16} color="#ffd700" />
            </div>
            <div>
              <p style={{ fontSize: 13, fontWeight: 600, color: '#e0dff0' }}>Administrator</p>
              <p style={{ fontSize: 11, color: '#8885a0' }}>Password Access</p>
            </div>
          </div>
        </header>
        <div style={{ padding: 32 }}>
          {children}
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
